<template>
  <button
    :class="buttonClasses"
    :type="type"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <template v-if="isSpinnerActive">
      <Spinner :class="spinnerClasses" />
    </template>

    <slot />
  </button>
</template>

<script>
import { computed } from '@vue/composition-api'
import { isEmpty } from 'lodash'

import Spinner from './Spinner'

export default {
  name: 'SanitasButton',

  components: {
    Spinner,
  },

  props: {
    type: {
      type: String,
      default: 'button',
    },

    primary: Boolean,
    secondary: Boolean,
    disabled: Boolean,
    fullWidthInXs: Boolean,
    isSpinnerActive: Boolean,
  },

  setup(props, context) {
    const buttonClasses = computed(() => ({
      'sanitas-button': true,
      'is-primary': props.primary,
      'is-secondary': !props.primary && props.secondary,
      'is-disabled': props.disabled,
      'is-spinning': props.isSpinnerActive,
      'has-text': !isEmpty(context.slots),
      'is-fullwidth-xs': props.fullWidthInXs,
    }))
    const spinnerClasses = computed(() => ({
      'spinner': true,
      'theme--white': props.primary,
      'theme--light-green': props.secondary,
    }))

    return {
      buttonClasses,
      spinnerClasses,
    }
  },
}
</script>

<style scoped>
.sanitas-button {
  position: relative; /* needed for the ripple-effect /**/
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 50px;
  width: auto;
  min-width: 120px;
  line-height: 24px;
  padding: 10px 36px 10px 26px;
  background: none;
  border-radius: 25px;
  font-weight: bold;
  font-size: 15px;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: 0.25px;
  outline: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: 'Open Sans', sans-serif;

  border: 2px solid darkred;
  background-color: lightgreen;
  color: darkred;

  .icon {
    margin-right: 10px;
    margin-top: -5px;
  }

  &:not(.has-text) {
    padding-right: 26px;
    .icon {
      margin-right: 0;
    }
  }

  .spinner {
    margin: 2px 8px 0 -15px; /**/
  }

  &:not(.is-spinning):not(.has-icon) {
    padding-left: 36px;
  }

  &.is-spinning {
    pointer-events: none; /* this prevents ripple-effects on spinning buttons */
  }

  &.is-primary {
    background-color: var(--c-light-green);
    border: 2px solid var(--c-light-green);
    color: var(--c-white);
    fill: var(--c-white);

    &:disabled,
    &.is-disabled {
      background-color: var(--c-light-grey);
      border: 2px solid var(--c-light-grey);
      color: #f1f1f1; /* equals rgba(255, 255, 255, 0.6) on #DDDDDD background */

      &:hover {
        cursor: default;
        background-color: var(--c-light-grey);
        border: 2px solid var(--c-light-grey);
        color: #f1f1f1; /* equals rgba(255, 255, 255, 0.6) on #DDDDDD background */
      }
    }

    &.is-spinning {
      &:hover {
        cursor: default;
        background-color: var(--c-light-green);
        border: 2px solid var(--c-light-green);
        color: var(--c-white);
      }
    }

    &:hover {
      cursor: pointer;
      border-color: var(--c-green);
      background-color: var(--c-green);
      color: var(--c-white);
      fill: var(--c-white);
    }
  }

  &.is-secondary {
    background-color: transparent;
    border: 2px solid var(--c-light-green);
    color: var(--c-light-green);
    fill: var(--c-light-green);

    &:disabled,
    &.is-disabled {
      background-color: transparent;
      border: 2px solid var(--c-dark-grey);
      color: var(--c-dark-grey);
      opacity: 0.3;

      &:hover {
        cursor: default;
        background-color: transparent;
        border: 2px solid var(--c-dark-grey);
        color: var(--c-dark-grey);
      }
    }

    &.is-spinning {
      &:hover {
        cursor: default;
        background-color: transparent;
        border: 2px solid var(--c-light-green);
        color: var(--c-light-green);
      }
    }

    &:hover {
      cursor: pointer;
      background-color: var(--c-light-green);
      border: 2px solid var(--c-light-green);
      color: var(--c-white);
      fill: var(--c-white);
    }
  }

  &.is-fullwidth-xs {
    width: 100%;

    @media (min-width: 543px) {
      width: auto;
    }
  }
}
</style>
